import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Eye, Users, Calendar } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const AnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/analytics/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('خطا در دریافت آمار');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#D4AF37]">در حال بارگذاری آمار...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light text-white mb-2">
            آمار <span className="text-[#D4AF37]">بازدید سایت</span>
          </h1>
          <p className="text-zinc-400">مشاهده آمار کامل بازدیدکنندگان</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Eye className="text-[#D4AF37]" size={24} />
              <span className="text-xs text-zinc-400">کل بازدیدها</span>
            </div>
            <div className="text-3xl font-light text-white mb-1">
              {stats?.total_views?.toLocaleString('fa-IR') || 0}
            </div>
            <div className="text-sm text-zinc-500">از ابتدا</div>
          </div>

          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Calendar className="text-blue-400" size={24} />
              <span className="text-xs text-zinc-400">امروز</span>
            </div>
            <div className="text-3xl font-light text-white mb-1">
              {stats?.today_views?.toLocaleString('fa-IR') || 0}
            </div>
            <div className="text-sm text-zinc-500">بازدید</div>
          </div>

          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="text-green-400" size={24} />
              <span className="text-xs text-zinc-400">این هفته</span>
            </div>
            <div className="text-3xl font-light text-white mb-1">
              {stats?.week_views?.toLocaleString('fa-IR') || 0}
            </div>
            <div className="text-sm text-zinc-500">بازدید</div>
          </div>

          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="text-purple-400" size={24} />
              <span className="text-xs text-zinc-400">این ماه</span>
            </div>
            <div className="text-3xl font-light text-white mb-1">
              {stats?.month_views?.toLocaleString('fa-IR') || 0}
            </div>
            <div className="text-sm text-zinc-500">بازدید</div>
          </div>
        </div>

        {/* Top Pages */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-medium text-white mb-4 flex items-center gap-2">
              <BarChart3 className="text-[#D4AF37]" size={20} />
              صفحات پربازدید
            </h2>
            <div className="space-y-3">
              {stats?.top_pages?.map((page, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-[#D4AF37] font-medium">#{index + 1}</span>
                    <span className="text-white text-sm">{page._id || 'صفحه اصلی'}</span>
                  </div>
                  <span className="text-zinc-400 text-sm">{page.count?.toLocaleString('fa-IR')} بازدید</span>
                </div>
              )) || <div className="text-zinc-500 text-center py-4">داده‌ای موجود نیست</div>}
            </div>
          </div>

          {/* Daily Chart */}
          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-medium text-white mb-4 flex items-center gap-2">
              <TrendingUp className="text-[#D4AF37]" size={20} />
              بازدید ۳۰ روز گذشته
            </h2>
            <div className="space-y-2">
              {stats?.daily_views?.slice(-10).map((day, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-zinc-400 text-xs w-20">{day._id}</span>
                  <div className="flex-1 bg-white/5 rounded-full h-6 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] h-full rounded-full"
                      style={{ width: `${Math.min((day.count / Math.max(...(stats?.daily_views?.map(d => d.count) || [1]))) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-white text-sm w-12 text-left">{day.count?.toLocaleString('fa-IR')}</span>
                </div>
              )) || <div className="text-zinc-500 text-center py-4">داده‌ای موجود نیست</div>}
            </div>
          </div>
        </div>

        {/* Recent Visits */}
        <div className="glass-effect border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-medium text-white mb-4 flex items-center gap-2">
            <Users className="text-[#D4AF37]" size={20} />
            آخرین بازدیدها
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-right text-zinc-400 font-medium py-3 px-4">صفحه</th>
                  <th className="text-right text-zinc-400 font-medium py-3 px-4">زمان</th>
                  <th className="text-right text-zinc-400 font-medium py-3 px-4">Referrer</th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_visits?.slice(0, 20).map((visit, index) => (
                  <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-4 text-white text-sm">{visit.page || '/'}</td>
                    <td className="py-3 px-4 text-zinc-400 text-sm">
                      {new Date(visit.timestamp).toLocaleString('fa-IR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="py-3 px-4 text-zinc-500 text-xs truncate max-w-xs">
                      {visit.referrer || 'Direct'}
                    </td>
                  </tr>
                )) || (
                  <tr>
                    <td colSpan="3" className="text-center py-8 text-zinc-500">
                      هنوز بازدیدی ثبت نشده است
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
